export interface ReleaseNote {
  version: string
  date: string
  title: string
  highlights: string[]
  features: Array<{
    title: string
    description: string
  }>
  improvements: string[]
  bugFixes: string[]
}

export const releaseNotes: ReleaseNote[] = [
  {
    version: "2.5.0",
    date: "April 22, 2026",
    title: "Unified Navigation & Release Notes",
    highlights: [
      "Completely restructured sidebar navigation across all 22 applications for a streamlined experience",
      "New dedicated Release Notes page with automated update detection",
      "Standalone web browser mode in Search for distraction-free browsing",
      "AI-powered release notes generation for transparent platform updates",
    ],
    features: [
      {
        title: "Unified Sidebar Navigation",
        description: "A newly designed sidebar consolidating all AskFinz apps into an intuitive, role-based layout with 17 sections. Navigate between Dashboard, News, Chat, Search, Research, Finance, and more—all from a consistent sidebar experience.",
      },
      {
        title: "Release Notes Hub",
        description: "A full-page Release Notes view at askfinz.ai/release-notes with version timeline navigation, feature breakdowns, and a 'Check for Updates' button that detects new platform deployments.",
      },
      {
        title: "Standalone Web Browser",
        description: "Access the AskFinz mini-browser at search.askfinz.ai/browser as a standalone tool for web research without leaving the platform.",
      },
      {
        title: "Live News Feed Migration",
        description: "The Live TV Guide has moved to the News hub at news.askfinz.ai for a centralized media experience.",
      },
    ],
    improvements: [
      "Cross-app sidebar links now seamlessly navigate between different subdomains with authentication preserved",
      "New Chat session shortcut clears conversation state for a fresh start",
      "Admin-only sections intelligently hide from non-privileged users",
      "Role-based visibility reacts in real time to authentication state changes",
    ],
    bugFixes: [
      "Fixed sidebar not reflecting user login status across subdomains",
      "Fixed Release Notes dialog 'View All' link pointing to wrong subdomain",
    ],
  },
  {
    version: "2.4.0",
    date: "April 22, 2026",
    title: "Installers Portal & Platform Configuration",
    highlights: [
      "New centralized Installers hub for downloading AskFinz Desktop Browser and Extension",
      "Streamlined onboarding with direct installer access from the Dashboard",
      "Global search index available across all applications for fast app switching",
    ],
    features: [
      {
        title: "Installers Portal",
        description: "A dedicated page at askfinz.ai/installers providing quick access to the AskFinz Browser desktop application and the AskFinz Extension for Chrome-based browsers.",
      },
      {
        title: "Index Review Dashboard",
        description: "New authenticated Index Reviewer page allowing you to monitor what web content has been indexed by your personal knowledge base.",
      },
    ],
    improvements: [
      "Unified app shell layout with consistent header, sidebar, and footer across all portals",
      "Improved shared component library with new exported modules for reuse",
    ],
    bugFixes: [
      "Fixed ChatDrawer UI layout breaking between header and footer boundaries",
      "Resolved chat input form functionality regression from previous component refactor",
    ],
  },
  {
    version: "2.3.0",
    date: "April 21, 2026",
    title: "AI Orchestrator & Workflow Intelligence",
    highlights: [
      "Real-time, time-zone-aware AI assistant responses",
      "End-to-end Workflow Builder with visual flow creation",
      "Tool-driven orchestration enabling AI to execute complex multi-step tasks",
    ],
    features: [
      {
        title: "Time-Aware AI Assistant",
        description: "The AskFinz AI now understands your local timezone and provides contextually accurate date and time references in every response.",
      },
      {
        title: "Workflow Builder",
        description: "Create custom AI workflows using a visual drag-and-drop builder. Connect inputs, AI agents, and outputs to automate complex research and analysis tasks.",
      },
      {
        title: "Tool Execution Pipeline",
        description: "The AI assistant can now execute tools including MCP integrations, API calls, and database queries to deliver actionable results beyond simple text responses.",
      },
    ],
    improvements: [
      "Improved workflow template persistence — templates now save reliably to your dashboard",
      "Cleaner folder initialization for new workflow collections",
    ],
    bugFixes: [
      "Fixed Workflow Builder 502 Bad Gateway errors during flow execution",
      "Resolved duplicate 'Starter Project' entries in workflow dashboard",
    ],
  },
  {
    version: "2.2.0",
    date: "April 15, 2026",
    title: "Multi-Profile Browsing & Session Persistence",
    highlights: [
      "Multi-profile browsing with isolated cookie and session storage per profile",
      "Persistent tab sessions that survive browser restarts",
      "Speed Dial new tab page with most-visited and recently-viewed sites",
    ],
    features: [
      {
        title: "Browser Profiles",
        description: "Create and switch between multiple browsing profiles, each with completely isolated cookies, cache, and session data—perfect for managing multiple accounts or separating work and personal browsing.",
      },
      {
        title: "Speed Dial New Tab",
        description: "Blank new tabs now show a dynamically generated Speed Dial grid featuring your most visited and recently viewed domains for quick access.",
      },
      {
        title: "Persistent Tab Sessions",
        description: "Your open tabs are now continuously saved and automatically restored when you reopen the browser, so you never lose your research context.",
      },
    ],
    improvements: [
      "Stale-While-Revalidate caching for search queries eliminates loading stutter on repeated searches",
      "URL state is now fully preserved across page refreshes including search query and active tab",
      "Profile dropdown restyled as a sleek rounded pill badge in the tab bar",
    ],
    bugFixes: [
      "Fixed Google Sign-in authentication failures in the embedded browser",
      "Resolved toolbar UI elements disappearing on empty new tabs",
      "Fixed click-to-read narrator skipping and highlighting accuracy",
    ],
  },
  {
    version: "2.1.0",
    date: "April 12, 2026",
    title: "Desktop Browser Launch & Extension v9.9.32",
    highlights: [
      "AskFinz Desktop Browser officially launched with custom branded installer",
      "Complete audio narrator with click-to-read and sentence highlighting",
      "Real-time page translation integrated directly into the browser toolbar",
      "CORS infrastructure overhaul for seamless backend connectivity",
    ],
    features: [
      {
        title: "AskFinz Desktop Browser",
        description: "A fully branded Electron-based desktop browser with a 6-step installation wizard, comprehensive legal pages, and seamless integration with the AskFinz Chrome Extension for knowledge indexing.",
      },
      {
        title: "Audio Narrator",
        description: "Read any webpage aloud with a built-in narrator featuring sentence-level highlighting, click-to-read navigation, and voice selection. Works seamlessly in both the desktop browser and web mini-browser.",
      },
      {
        title: "Inline Page Translation",
        description: "Translate any webpage on-the-fly using the translation toggle in the browser toolbar, powered by the AskFinz translation backend.",
      },
      {
        title: "URL Navigation Sync",
        description: "The address bar now perfectly reflects the current page even after internal navigations within websites, keeping your browsing context always visible.",
      },
    ],
    improvements: [
      "Restructured Nginx CORS headers to eliminate duplicate header conflicts",
      "Improved iframe URL bridge for consistent navigation tracking",
      "Browser extension upgraded to v9.9.32 with dynamic versioning",
    ],
    bugFixes: [
      "Fixed CORS connectivity failures between frontend and backend microservices",
      "Fixed admin dashboard crashes from icon caching errors",
      "Resolved installer privilege escalation issues on Windows 11",
      "Fixed address bar and tab container stretching when loading long URLs",
    ],
  },
  {
    version: "2.0.0",
    date: "April 11, 2026",
    title: "Knowledge Indexing Engine & Explorer Graph",
    highlights: [
      "Powerful web crawling engine with spider mode for deep content discovery",
      "Interactive Explorer Graph for visualizing indexed knowledge connections",
      "PDF extraction supporting SEC filings, academic papers, and more",
      "Domain analytics with 15+ metrics per indexed domain",
    ],
    features: [
      {
        title: "Spider Crawl Engine",
        description: "Automatically discover and index connected pages across websites. The engine intelligently follows links, handles JavaScript-heavy sites, and respects rate limiting for reliable deep crawling.",
      },
      {
        title: "Explorer Graph",
        description: "Visualize your indexed knowledge as an interactive node graph. Explore connections between domains, zoom into clusters, and click nodes to instantly browse their content.",
      },
      {
        title: "PDF Extraction",
        description: "Automatically extract and index content from PDF documents including SEC filings, academic papers from PubMed, and e-books from Scribd.",
      },
      {
        title: "Domain Analytics Dashboard",
        description: "15+ metrics per indexed domain including page count, content quality scores, crawl depth, and freshness indicators to help you manage your knowledge base effectively.",
      },
    ],
    improvements: [
      "Added dual index workers for parallel content processing",
      "Improved domain browsing with live polling and explorer integration",
      "Content quality filtering automatically removes low-value pages",
      "Wikipedia crawling with specialized subdomain extraction",
    ],
    bugFixes: [
      "Fixed Redis database mismatch between index workers",
      "Resolved protocol-relative URL handling for Wikipedia links",
      "Fixed SEC.gov 403 errors with User-Agent compliance",
      "Fixed explorer graph node shaking and zoom reset on hover",
    ],
  },
  {
    version: "1.9.0",
    date: "April 5, 2026",
    title: "MCP Server & N-Series Search Pipeline",
    highlights: [
      "MCP Server upgrade with 30+ container management tools",
      "13 major improvements to the search and indexing pipeline",
      "Full project synchronization across all backend microservices",
    ],
    features: [
      {
        title: "MCP Server v9.7",
        description: "A comprehensive upgrade to the Model Context Protocol server with health checks across all containers, service restart capabilities, and direct Docker command execution for seamless infrastructure management.",
      },
      {
        title: "N-Series Search Pipeline",
        description: "13 search improvements including smarter query parsing, more relevant result ranking, and faster response times for knowledge-base lookups.",
      },
      {
        title: "Universal AJAX Domain Detection",
        description: "Automatically detects JavaScript-heavy websites like PubMed and Scopus and switches to rendered DOM crawling for complete content capture.",
      },
    ],
    improvements: [
      "Edge-proxied spider mode for enhanced crawling reliability",
      "Dashboard cleanup with streamlined UI components",
      "Updated documentation with domain compatibility matrix",
    ],
    bugFixes: [
      "Fixed stale UI elements in domain analytics views",
      "Resolved MCP Server build failures",
      "Fixed Worker v2.1.0 User-Agent compliance issues",
    ],
  },
  {
    version: "1.0.0",
    date: "March 16, 2026",
    title: "AskFinz Platform Launch",
    highlights: [
      "Initial launch of the AskFinz AI platform with 22 specialized web applications",
      "Unified authentication across all subdomains with OTP and 2FA support",
      "Full-stack monorepo architecture with shared component library",
    ],
    features: [
      {
        title: "Multi-App Platform",
        description: "22 specialized web applications covering Search, Chat, Finance, Research, Education, Medical, Real Estate, Travel, Code, Data Analytics, Social Media, Storage, Streaming, and more.",
      },
      {
        title: "Unified Authentication",
        description: "Single sign-on across all AskFinz applications with email OTP verification, two-factor authentication, and cross-subdomain session synchronization.",
      },
      {
        title: "Shared Component Library",
        description: "A comprehensive UI component library ensuring brand consistency across all applications with themes, navigation, search, notifications, and accessibility features.",
      },
    ],
    improvements: [
      "Modern dark theme with glassmorphism effects throughout the platform",
      "Responsive design optimized for desktop, tablet, and mobile devices",
    ],
    bugFixes: [],
  },
]

export const getLatestReleaseNote = (): ReleaseNote => {
  return releaseNotes[0]
}

